import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "../constants/queryKeys";
import { AccountType } from "../store/actions/dashboard";
import { getSubscriptionStatus } from "../store/actions/subscriptions";
import { useAppDispatch } from "../store/hooks";
import {
  activeUserIdAtom,
  isActiveUserArtistAtom,
  isActiveUserEngineerOrStudioManagerAtom,
  isActiveUserEngineerStudioManagerOrArtistAtom,
} from "../atoms/user/activeUserAtom";
import { useAtomValue } from "jotai";
import {
  Subscription,
  SUBSCRIPTION_PLAN,
  SUBSCRIPTION_STATUS,
} from "../store/models/subscription";

interface useGetSubscriptionStatusProps {
  accountType?: AccountType;
}

export interface ClientSubscription extends Subscription {
  hasActiveSubscription?: boolean;
  hasActiveArtistSubscription?: boolean;
  hasPlatinum?: boolean;
  hasArtistPlatinum?: boolean;
  showUpgradeButton?: boolean;
  showArtistUpgradeButton?: boolean;
}
// Get the engineer subscription by default
export const useGetSubscriptionStatus = (
  params: useGetSubscriptionStatusProps | undefined = {},
) => {
  const { accountType = AccountType.ENGINEER } = params;
  const dispatch = useAppDispatch();
  const activeUserId = useAtomValue(activeUserIdAtom);
  const hasSubscription = useAtomValue(
    isActiveUserEngineerStudioManagerOrArtistAtom,
  );
  const isEngineerOrStudioManager = useAtomValue(
    isActiveUserEngineerOrStudioManagerAtom,
  );
  const isArtist = useAtomValue(isActiveUserArtistAtom);

  return useQuery({
    queryKey: [QUERY_KEYS.GET_SUBSCRIPTION_STATUS, activeUserId, accountType],
    queryFn: async () => {
      const result = await dispatch(
        getSubscriptionStatus({
          refetch_subs_status: true, // Why would this ever be false?
          account_type: accountType,
        }),
      ).unwrap();
      const validatedResult: ClientSubscription = { ...result };
      if (
        accountType === AccountType.ENGINEER ||
        accountType === AccountType.STUDIO
      ) {
        // Has Subscription Check
        validatedResult.hasActiveSubscription =
          result?.subscription_plan_choice !==
            SUBSCRIPTION_PLAN.LEGACY_BASIC_PLAN &&
          result?.subscription_plan_choice !==
            SUBSCRIPTION_PLAN.ESSENTIALS_PLAN &&
          result?.subscription_plan_choice !==
            SUBSCRIPTION_PLAN.NO_SUBSCRIPTION_PLAN &&
          result?.stripe_subscription_status === SUBSCRIPTION_STATUS.ACTIVE;
        // Requires Upgrade Check
        validatedResult.showUpgradeButton =
          result?.stripe_subscription_status &&
          result?.subscription_plan_choice !==
            SUBSCRIPTION_PLAN.PLATINUM_PLAN_MONTHLY &&
          result?.subscription_plan_choice !==
            SUBSCRIPTION_PLAN.PLATINUM_PLAN_QUARTERLY &&
          result?.subscription_plan_choice !==
            SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY;

        // Platinum Check
        validatedResult.hasPlatinum =
          result?.subscription_plan_choice ===
            SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY ||
          result?.subscription_plan_choice ===
            SUBSCRIPTION_PLAN.PLATINUM_PLAN_MONTHLY ||
          result?.subscription_plan_choice ===
            SUBSCRIPTION_PLAN.PLATINUM_PLAN_QUARTERLY;
      } else if (accountType === AccountType.ARTIST) {
        validatedResult.hasArtistPlatinum =
          result?.subscription_plan_choice ===
          SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY;
        validatedResult.hasActiveArtistSubscription =
          result?.subscription_plan_choice
            ? [
                SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY,
                SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN,
              ].includes(result.subscription_plan_choice)
            : false;
        validatedResult.showArtistUpgradeButton =
          validatedResult.hasActiveArtistSubscription &&
          !validatedResult.hasArtistPlatinum;
      }
      return validatedResult;
    },
    enabled:
      hasSubscription &&
      (accountType === AccountType.ARTIST && isArtist
        ? true
        : isEngineerOrStudioManager),
  });
};
